import { IonIcon, IonModal } from "@ionic/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TemplateStructureItemEntity } from "@/lib/backend/api";
import { usePreferredHaptics } from "@/lib/hooks/use-preferred-haptics";
import {
  chevronRightIcon,
  documentTextIcon,
  folderIcon,
  magnifyingGlassIcon,
  xMarkIcon,
} from "@/lib/icons/@heroicons/react/24/outline";
import { useMessageTemplateVariables } from "@/lib/query/message-template";
import { cx } from "@/lib/style/cva.config";
import { type Platform, type TemplateItem, useTemplateSelection } from "./use-template-selection";

interface TemplateSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  platform: Platform;
  onSelectTemplate: (template: TemplateStructureItemEntity) => void;
}

export function TemplateSelectionModal({
  isOpen,
  onClose,
  platform,
  onSelectTemplate,
}: TemplateSelectionModalProps) {
  const { t } = useTranslation();
  const haptics = usePreferredHaptics();
  const modalRef = useRef<HTMLIonModalElement>(null);
  const [internalSearch, setInternalSearch] = useState("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const {
    currentPath,
    searchQuery,
    items,
    isLoading,
    navigateToFolder,
    navigateToPathIndex,
    setSearchQuery,
    reset,
    getTemplateById,
  } = useTemplateSelection({ platform });

  // Fetch template variables for rendering
  const { data: variablesData } = useMessageTemplateVariables();

  // Create a map of variableName -> friendlyName for quick lookup
  const variableMap = useMemo(() => {
    const map = new Map<string, string>();
    variablesData?.items?.forEach((variable) => {
      map.set(variable.variableName, variable.friendlyName);
    });
    return map;
  }, [variablesData?.items]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setInternalSearch("");
      reset();
    }
  }, [isOpen, reset]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInternalSearch(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, 300);
  };

  const handleClearSearch = () => {
    setInternalSearch("");
    setSearchQuery("");
  };

  const handleClose = () => {
    haptics.impact();
    onClose();
  };

  const handleItemClick = (item: TemplateItem) => {
    haptics.impact();

    if (item.isFolder) {
      navigateToFolder(item.id, item.name);
    } else if (item.isApprovedForPlatform) {
      const template = getTemplateById(item.id);
      if (template) {
        onSelectTemplate(template);
        onClose();
      }
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    haptics.impact();
    navigateToPathIndex(index);
  };

  // Parse template text and render with styled tokens
  const renderTemplateText = (text: string): React.ReactNode => {
    const parts = text.split(/(\{\{[^}]+\}\})/g);

    return parts.map((part, index) => {
      const variableMatch = part.match(/^\{\{([^}]+)\}\}$/);
      if (variableMatch) {
        const variableName = variableMatch[1];
        const friendlyName = variableMap.get(variableName) || variableName;
        return (
          <span
            key={index}
            className="inline-flex items-center rounded bg-brand-100 px-1 py-0.5 text-xs font-medium text-brand-700"
          >
            {friendlyName}
          </span>
        );
      }
      // Handle newlines in text
      return part.split("\n").map((line, lineIndex, arr) => (
        <span key={`${index}-${lineIndex}`}>
          {line}
          {lineIndex < arr.length - 1 && <br />}
        </span>
      ));
    });
  };

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      onDidDismiss={onClose}
      initialBreakpoint={1}
      breakpoints={[0, 1]}
      className="template-selection-modal"
    >
      <div className="flex h-full flex-col overflow-hidden rounded-t-[10px] bg-white">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4">
          {/* Empty spacer for centering */}
          <div className="size-6" />

          {/* Title */}
          <h2 className="text-lg font-semibold leading-[1.6] text-txt-primary">
            {t("routes:conversations.select-template")}
          </h2>

          {/* Close button */}
          <button
            type="button"
            onClick={handleClose}
            className="flex size-6 items-center justify-center text-txt-primary transition-opacity active:opacity-70"
          >
            <IonIcon icon={xMarkIcon} className="size-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5 pt-0">
          {/* Search bar */}
          <div className="flex items-center gap-2.5 rounded-[10px] border-[1.25px] border-bordercolor-primary px-4 py-2.5">
            <IonIcon icon={magnifyingGlassIcon} className="size-5 shrink-0 text-txt-primary" />
            <input
              type="text"
              value={internalSearch}
              onChange={handleSearchChange}
              placeholder={t("routes:conversations.template-search")}
              className="flex-1 bg-transparent text-[15px] font-medium leading-[1.6] text-txt-primary outline-none placeholder:text-txt-placeholder"
            />
            {internalSearch && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="flex size-5 items-center justify-center text-txt-tertiary transition-opacity active:opacity-70"
              >
                <IonIcon icon={xMarkIcon} className="size-5" />
              </button>
            )}
          </div>

          {/* Breadcrumb navigation */}
          <nav className="flex flex-wrap items-center gap-1 text-sm">
            <button
              type="button"
              onClick={() => handleBreadcrumbClick(-1)}
              className={cx(
                "font-medium transition-colors active:opacity-70",
                currentPath.length === 0 ? "text-txt-primary" : "text-txt-brand",
              )}
            >
              {t("routes:conversations.template-root")}
            </button>
            {currentPath.map((segment, index) => (
              <span key={index} className="flex items-center gap-1">
                <IonIcon icon={chevronRightIcon} className="size-4 text-txt-tertiary" />
                {index === currentPath.length - 1 ? (
                  <span className="font-medium text-txt-primary">{segment}</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleBreadcrumbClick(index)}
                    className="font-medium text-txt-brand transition-colors active:opacity-70"
                  >
                    {segment}
                  </button>
                )}
              </span>
            ))}
          </nav>

          {/* Templates/Folders list */}
          <div className="flex flex-col">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-txt-secondary">{t("common:loading")}</div>
              </div>
            ) : items.length === 0 && (currentPath.length > 0 || searchQuery) ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-txt-secondary">
                  {searchQuery
                    ? t("routes:conversations.template-no-results")
                    : t("routes:conversations.template-no-results")}
                </p>
              </div>
            ) : (
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleItemClick(item)}
                  disabled={!item.isFolder && !item.isApprovedForPlatform}
                  className={cx(
                    "flex items-start gap-3 border-b border-bordercolor-primary py-4 text-left transition-colors",
                    item.isFolder || item.isApprovedForPlatform
                      ? "active:bg-gray-50"
                      : "cursor-not-allowed opacity-50",
                  )}
                >
                  {/* Icon */}
                  <IonIcon
                    icon={item.isFolder ? folderIcon : documentTextIcon}
                    className={cx(
                      "mt-0.5 size-5 shrink-0",
                      item.isFolder ? "text-txt-tertiary" : "text-txt-brand",
                    )}
                  />

                  {/* Content */}
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="text-base font-semibold leading-[1.5] text-txt-primary">
                      {item.name}
                    </span>
                    {!item.isFolder && item.previewText && (
                      <div className="line-clamp-2 text-sm leading-[1.4] text-txt-secondary">
                        {renderTemplateText(item.previewText)}
                      </div>
                    )}
                    {!item.isFolder && !item.isApprovedForPlatform && (
                      <span className="text-xs text-amber-600">
                        {t("routes:conversations.template-not-approved")}
                      </span>
                    )}
                  </div>

                  {/* Chevron for folders */}
                  {item.isFolder && (
                    <IonIcon
                      icon={chevronRightIcon}
                      className="size-5 shrink-0 text-txt-tertiary"
                    />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </IonModal>
  );
}
